import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { inspectionApi } from "./api";
import { TierBadge } from "./TierBadge";
import { RoomView } from "./RoomView";
import { InspectionSummary } from "./InspectionSummary";

const roomDone = (insp, room) =>
  room.shots.every((s) => !s.required || (insp.photos?.[room.key]?.[s.key] || []).length) &&
  room.items.every((it) => insp.findings?.[`${room.key}::${it.key}`]);

export const InspectionRunner = ({ inspectionId }) => {
  const [insp, setInsp] = useState(null);
  const [idx, setIdx] = useState(0);

  const load = useCallback(async () => {
    try {
      setInsp((await inspectionApi.get(inspectionId)).inspection);
    } catch (e) {
      toast.error(e.message);
    }
  }, [inspectionId]);
  useEffect(() => { load(); }, [load]);

  if (!insp) return <div className="insp-loading" data-testid="insp-loading">Loading inspection…</div>;
  const rooms = insp.rooms;
  const atSummary = idx >= rooms.length;
  const locked = insp.status === "completed";

  const onUploaded = (roomKey, shotKey, photoId) =>
    setInsp((p) => {
      const photos = { ...p.photos, [roomKey]: { ...(p.photos?.[roomKey] || {}) } };
      photos[roomKey][shotKey] = [...(photos[roomKey][shotKey] || []), photoId];
      return { ...p, photos };
    });

  const onSet = async (roomKey, itemKey, condition, note) => {
    if (locked) return toast.info("Inspection is locked");
    try {
      const res = await inspectionApi.finding(insp.inspection_id, { room_key: roomKey, item_key: itemKey, condition, note });
      setInsp((p) => ({ ...p, classification: res.classification, progress: res.progress,
        findings: { ...p.findings, [`${roomKey}::${itemKey}`]: { condition, note } } }));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const go = (i) => { setIdx(Math.max(0, Math.min(rooms.length, i))); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const openSummary = async () => { await load(); go(rooms.length); };

  return (
    <div className="insp-runner" data-testid="insp-runner">
      <header className="insp-head">
        <div className="insp-head__addr" data-testid="insp-address">{insp.address}{insp.unit_label ? ` · Unit ${insp.unit_label}` : ""}</div>
        <TierBadge cls={insp.classification} compact />
      </header>
      <nav className="insp-rail" data-testid="insp-room-rail">
        {rooms.map((r, i) => (
          <button key={r.key} type="button" onClick={() => go(i)} data-testid={`insp-rail-${r.key}`}
                  className={`insp-rail__chip ${i === idx ? "on" : ""} ${roomDone(insp, r) ? "done" : ""}`}>
            {roomDone(insp, r) ? "✓ " : ""}{r.label}
          </button>
        ))}
        <button type="button" onClick={openSummary} data-testid="insp-rail-summary" className={`insp-rail__chip ${atSummary ? "on" : ""}`}>Result</button>
      </nav>
      {atSummary
        ? <InspectionSummary insp={insp} onJump={go} onCompleted={load} />
        : <RoomView insp={insp} room={rooms[idx]} onUploaded={onUploaded} onSet={onSet}
                    onDims={(rk, val, spec) => setInsp((p) => ({ ...p, dimensions: { ...(p.dimensions || {}), [rk]: val }, auto_spec: spec }))} />}
      {!atSummary && (
        <footer className="insp-foot">
          <button type="button" className="insp-btn" disabled={idx === 0} onClick={() => go(idx - 1)} data-testid="insp-prev-room">← Previous room</button>
          <button type="button" className="insp-btn insp-btn--primary" onClick={() => (idx === rooms.length - 1 ? openSummary() : go(idx + 1))} data-testid="insp-next-room">
            {idx === rooms.length - 1 ? "See result →" : "Next room →"}
          </button>
        </footer>
      )}
    </div>
  );
};
